import type { components } from '@leanin/api-client';

export type ActionKey =
  components['schemas']['RecordOnboardingActionBody']['actionKey'];

export type ChecklistItemSignal = 'derived' | 'visit';

export type ChecklistItem = {
  readonly title: string;
  readonly sub: string;
  readonly cta: string;
  readonly href: string;
  readonly signal: ChecklistItemSignal;
};

// Per-action_key copy / CTA / link. The user's step list (an ordered
// array of action_keys) comes from the server; the frontend resolves each
// key through this map to render the card and visit-tracker. One canonical
// copy bundle per key — persona-specific copy belongs in a distinct
// action_key (e.g. `first_network_post`), not a per-persona variant here.
export const STEP_CONTENT_BY_KEY: Readonly<Record<ActionKey, ChecklistItem>> = {
  confirm_profile: {
    title: 'Add your photo & bio',
    sub: 'Add a photo and a couple of lines about you.',
    cta: 'Add my photo & bio',
    href: '/settings/account',
    signal: 'derived',
  },
  first_post: {
    title: 'Say hello',
    sub: "Introduce yourself, share what brought you here, or tell the community what you're interested in exploring.",
    cta: 'Create a post',
    // `?compose=true` auto-opens + focuses the feed composer on landing
    // (handled in the /feed route) — otherwise clicking lands on a feed the
    // user is often already viewing and "nothing happens". Boolean-shaped
    // value: string hrefs JSON-round-trip through the router, so `1`
    // arrives as the string '"1"'.
    href: '/feed?compose=true',
    signal: 'derived',
  },
  joined_circle: {
    title: 'Find a Circle to join',
    sub: 'Connect with people who share your goals, interests, and experiences.',
    cta: 'Join a Circle',
    href: '/circles',
    signal: 'derived',
  },
  joined_group: {
    title: 'Join a discussion group',
    sub: 'Find a topic that interests you — from AI to working parents to career growth — and join the conversation.',
    cta: 'Join a Group',
    href: '/groups',
    signal: 'derived',
  },
  joined_network: {
    title: 'Find a Network to join',
    sub: "Your Circle is just the beginning. Networks bring your Circle into a bigger community - local events, speaker series, and connections across 100+ cities and 40 countries.",
    cta: 'Browse Networks',
    href: '/networks',
    signal: 'derived',
  },
  visit_resources: {
    title: 'Explore resources',
    sub: 'Explore our growing library of expert talks, skill-building lessons, discussion guides, how-tos, and more resources to help you lead.',
    cta: 'Explore resources',
    href: '/resources',
    signal: 'visit',
  },
  visit_meeting_guides: {
    title: 'Check out our Circle meeting guides',
    sub: 'Ready-to-run agendas and discussion guides for every kind of Circle meeting.',
    cta: 'View guides',
    href: '/resources?scope=leadership_library',
    signal: 'visit',
  },
  complete_circle_profile: {
    title: 'Add a photo and description to your Circle',
    sub: 'A photo and a description help members recognize the Circle as theirs.',
    cta: 'Edit Circle',
    href: '/circles',
    // Completes when the circle actually HAS an image + description —
    // derived server-side on progress reads (get-progress self-heal), not
    // by visiting the admin screen.
    signal: 'derived',
  },
  attend_training: {
    title: 'Attend a training',
    sub: 'Join an info session to get all the information and practical advice you need to launch your Circle.',
    cta: 'Sign up',
    href: '/events?tab=platform',
    signal: 'visit',
  },
  invite_circle_members: {
    title: 'Invite Circle members',
    sub: 'Easily send invitations to new Circle members.',
    cta: 'Invite members',
    href: '/circles',
    signal: 'derived',
  },
  plan_first_meeting: {
    title: 'Plan your first meeting',
    sub: 'Check out our first meeting guide to guide you step-by-step through your first Circle meeting.',
    cta: 'View meeting guide',
    // Patched to the environment's first-meeting guide resource (its id is
    // per-environment config, surfaced on /me/onboarding-progress). The
    // library list is the fallback when unconfigured.
    href: '/resources',
    signal: 'visit',
  },
  visit_hub: {
    title: "Request to join the Network Leaders' Hub",
    sub: 'The Hub is where Network leaders trade training, templates, and support.',
    cta: 'Open Hub',
    href: '/networks',
    // Completes when she requests to join (or joins) the Hub — recorded by
    // the onboarding event subscriber — not by visiting the page.
    signal: 'derived',
  },
  update_network_page: {
    title: 'Add a photo and description to your Network page',
    sub: 'Help members discover your Network and understand what your Network is all about.',
    cta: 'Manage page',
    href: '/networks',
    // Completes when the network page actually HAS an image + description —
    // derived server-side on progress reads (get-progress self-heal), not
    // by visiting the manage screen.
    signal: 'derived',
  },
  add_event: {
    title: 'Add your first event',
    sub: 'Share an upcoming gathering, meeting, or event with your community.',
    cta: 'Add event',
    // The create-event form. Patched to `?groupId=<network>` when the leader
    // has a network so the event is pre-attributed to it.
    href: '/events/new',
    signal: 'derived',
  },
  invite_network_members: {
    title: 'Invite members to join your Network',
    sub: 'Invite members who are not yet on the platform so they can connect with your community and stay engaged with your Network.',
    cta: 'Invite members',
    href: '/networks',
    signal: 'derived',
  },
};

// Replaces the static directory href in STEP_CONTENT_BY_KEY with a deep
// link to the viewer's actual group when the step requires leader context.
// Each "manage" step targets the group's admin screen (the section the step
// is about), not the public detail page — that's where a leader actually
// edits the page, invites members, etc. Returns the original href when the
// user has no leader group of the relevant kind (the directory list is then
// the right fallback).
export function patchChecklistHref(
  key: ActionKey,
  baseHref: string,
  ctx: {
    readonly leaderCircleId?: string | null;
    readonly leaderNetworkId?: string | null;
    readonly hubNetworkId?: string | null;
    readonly meetingGuideResourceId?: string | null;
  },
): string {
  // The Network Leaders Hub is a specific network (its id is per-environment
  // config, surfaced on /me/onboarding-progress). Fall back to the directory
  // when unconfigured.
  if (key === 'visit_hub' && ctx.hubNetworkId != null) {
    return `/networks/${ctx.hubNetworkId}?tab=overview`;
  }
  // Same shape for the first-meeting guide: a specific resource whose id is
  // per-environment config. Fall back to the library when unconfigured.
  if (key === 'plan_first_meeting' && ctx.meetingGuideResourceId != null) {
    return `/resources/${ctx.meetingGuideResourceId}`;
  }
  if (ctx.leaderCircleId != null) {
    if (key === 'complete_circle_profile')
      return `/circles/${ctx.leaderCircleId}/admin?section=overview`;
    if (key === 'invite_circle_members')
      return `/circles/${ctx.leaderCircleId}/admin?section=members&invite=true`;
  }
  if (ctx.leaderNetworkId != null) {
    if (key === 'update_network_page')
      return `/networks/${ctx.leaderNetworkId}/admin?section=overview`;
    if (key === 'invite_network_members')
      return `/networks/${ctx.leaderNetworkId}/admin?section=members&invite=true`;
    if (key === 'add_event')
      return `/events/new?groupId=${ctx.leaderNetworkId}`;
  }
  return baseHref;
}
